// Merge freshly-loaded server rows with any still-pending optimistic items so a
// reload (which replaces a whole option tab) never wipes concurrent background
// work. Server rows get a stable localId; pending creates are appended; pending
// updates overlay their optimistic values; items mid-delete are hidden but kept
// (so they still count as pending) until their DELETE settles.
// Everything FoodItemCard actually reads. Nested objects (scaled_nutrition,
// food_details, recipe_details) are rebuilt on every load and so are never
// reference-equal — compare the values the card derives from them instead.
const RENDERED_FIELDS = [
  "id",
  "deleteId",
  "food_id",
  "food_name",
  "common_name",
  "quantity",
  "quantity_type",
  "quantity_type_id",
  "intake_time",
  "protein",
  "carbs",
  "fats",
  "fiber",
  "sugar",
  "calories",
  "_updated",
];

function sameRenderedRow(a, b) {
  if (a._sync?.status !== b._sync?.status) return false;
  if (a._sync?.op !== b._sync?.op) return false;
  if (a._sync?.error !== b._sync?.error) return false;
  if (RENDERED_FIELDS.some((k) => a[k] !== b[k])) return false;
  // The card prefers scaled_nutrition over the flat macros, so it has to agree
  // too — but only on the six numbers actually shown.
  const as = a.scaled_nutrition;
  const bs = b.scaled_nutrition;
  if (!as || !bs) return as === bs;
  return (
    (as.proteins ?? as.protein) === (bs.proteins ?? bs.protein) &&
    as.carbs === bs.carbs &&
    as.fats === bs.fats &&
    as.fiber === bs.fiber &&
    (as.sugars ?? as.sugar) === (bs.sugars ?? bs.sugar) &&
    as.calories === bs.calories
  );
}

export function mergeOptimisticRows(prevItems = [], serverRows = []) {
  const idOf = (it) => String(it.deleteId || it.id);
  const hasServerId = (it) => (it.deleteId ?? it.id) != null;
  // The server keys one row per food per option, so food_id is a reliable
  // secondary identity when the row id itself has churned (the server upserts
  // on update: old row id retired, new one issued).
  const foodKeyOf = (it) => (it.food_id != null ? String(it.food_id) : null);

  const serverIds = new Set(serverRows.filter(hasServerId).map(idOf));
  const serverFoodKeys = new Set(serverRows.map(foodKeyOf).filter(Boolean));

  // A row created optimistically keeps the localId it was born with even after
  // it adopts a server id, so its React key never changes and the card is never
  // remounted. For 'synced' rows whose id the server has since churned, the
  // fetched twin (matched by food) inherits the localId instead.
  const localIdByServerId = new Map();
  const localIdByFoodKey = new Map();
  prevItems.forEach((it) => {
    if (!it.localId) return;
    if (hasServerId(it)) localIdByServerId.set(idOf(it), it.localId);
    if (it._sync?.status === "synced" && !(hasServerId(it) && serverIds.has(idOf(it)))) {
      const key = foodKeyOf(it);
      if (key) localIdByFoodKey.set(key, it.localId);
    }
  });
  const prevByLocalId = new Map();
  prevItems.forEach((it) => {
    if (it.localId) prevByLocalId.set(it.localId, it);
  });

  const rows = serverRows.map((r) => {
    const byId = localIdByServerId.get(idOf(r));
    let byFood = null;
    if (!byId) {
      const key = foodKeyOf(r);
      if (key && localIdByFoodKey.has(key)) {
        byFood = localIdByFoodKey.get(key);
        localIdByFoodKey.delete(key); // one orphan adopts at most one twin
      }
    }
    const localId = byId || byFood || r.localId || String(r.deleteId || r.id);
    const prev = prevByLocalId.get(localId);
    // `_updated` marks a real user edit (it drives the card's 3s highlight); a
    // rebuild must inherit it rather than invent a new one.
    const next = { ...r, localId, _updated: prev?._updated };
    // Reuse the previous object when nothing the card renders has changed, so
    // editing one card doesn't hand every other card a new prop identity and
    // re-render the whole list underneath the user.
    return prev && sameRenderedRow(prev, next) ? prev : next;
  });

  // Any row carrying a live _sync marker is protected for as long as the marker
  // lives: it overlays its server twin, and survives even if the server hasn't
  // caught up yet. Runners keep the marker until their refetch merge lands, so a
  // freshly-saved card can never blink out during the success->refetch window.
  const deletingIds = new Set();
  const protectedById = new Map();
  const pendingCreates = [];
  prevItems.forEach((it) => {
    const status = it._sync?.status;
    if (!status) return;
    if (status === "deleting") {
      deletingIds.add(idOf(it));
      return;
    }
    // A synced row whose server twin now lives under a DIFFERENT id (upsert
    // churn) is superseded by that twin — drop the stale copy; its localId was
    // already transplanted onto the twin above. Without this, every modify
    // left a permanent ghost card next to the real row.
    if (status === "synced") {
      const key = foodKeyOf(it);
      const idStillPresent = hasServerId(it) && serverIds.has(idOf(it));
      if (!idStillPresent && key && serverFoodKeys.has(key)) return;
    }
    // No server id yet (create still in flight, or one that failed): it has no
    // twin to overlay, so carry it through as-is.
    if (!hasServerId(it)) {
      pendingCreates.push(it);
      return;
    }
    protectedById.set(idOf(it), it);
  });

  const visibleServer = rows
    .filter((r) => !deletingIds.has(idOf(r)))
    .map((r) => {
      const held = protectedById.get(idOf(r));
      if (!held) return r;
      // Return the held object itself when it already has a localId — spreading
      // it would mint a new identity on every merge and re-render the card.
      return held.localId ? held : { ...held, localId: r.localId };
    });
  const orphanProtected = [...protectedById.values()].filter(
    (it) => !serverIds.has(idOf(it))
  );

  const deletingOverlays = prevItems.filter((it) => it._sync?.status === "deleting");

  const out = [
    ...visibleServer,
    ...orphanProtected,
    ...deletingOverlays,
    ...pendingCreates,
  ];

  // Hard invariant: localId is the React key for these rows. Two entries can
  // still resolve to one localId if the meal tree ever holds duplicate rows for
  // a single server id. Collapse rather than render overlapping cards.
  const seen = new Set();
  return out.filter((it) => {
    if (!it.localId) return true;
    if (seen.has(it.localId)) return false;
    seen.add(it.localId);
    return true;
  });
}
